import React, { useState } from 'react';
import { Alert } from '../alerts/detect';
import { ALERT_KINDS, UseAlertsResult } from '../alerts/useAlerts';
import './AlertPanel.css';

interface AlertPanelProps extends UseAlertsResult {}

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const SEVERITY_ICON: Record<Alert['severity'], string> = {
  info: '•',
  warn: '▲',
  urgent: '■',
};

export const AlertPanel: React.FC<AlertPanelProps> = ({
  alerts,
  settings,
  setEnabled,
  setSound,
  toggleKind,
  dismiss,
  clearAll,
}) => {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div className="al-panel">
      <div className="al-header">
        <h3>Alerts</h3>

        <label className="al-toggle">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          On
        </label>

        <label className="al-toggle">
          <input
            type="checkbox"
            checked={settings.sound}
            onChange={(e) => setSound(e.target.checked)}
          />
          Sound
        </label>

        <button
          type="button"
          className="al-link"
          onClick={() => setShowSettings((v) => !v)}
          aria-expanded={showSettings}
        >
          {showSettings ? 'Done' : 'Choose alerts'}
        </button>

        {alerts.length > 0 && (
          <button type="button" className="al-link al-clear" onClick={clearAll}>
            Clear
          </button>
        )}
      </div>

      {showSettings && (
        <div className="al-kinds">
          {ALERT_KINDS.map(({ kind, label }) => (
            <label key={kind} className="al-toggle">
              <input
                type="checkbox"
                checked={settings.kinds[kind] !== false}
                onChange={() => toggleKind(kind)}
              />
              {label}
            </label>
          ))}
        </div>
      )}

      {!settings.enabled ? (
        <div className="al-empty">Alerts are off.</div>
      ) : alerts.length === 0 ? (
        <div className="al-empty">
          Nothing to report. Mark karts in the grid to watch them.
        </div>
      ) : (
        <ul className="al-list">
          {alerts.map((alert) => (
            <li key={alert.id} className={`al-item al-item--${alert.severity}`}>
              <span className="al-icon" aria-hidden>
                {SEVERITY_ICON[alert.severity]}
              </span>
              <span className="al-time">{formatTime(alert.at)}</span>
              <span className="al-message">{alert.message}</span>
              <button
                type="button"
                className="al-dismiss"
                onClick={() => dismiss(alert.id)}
                aria-label={`Dismiss: ${alert.message}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AlertPanel;
